import React, { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LoginContext } from '../../../context/LoginContext';

const DEFAULT_MESSAGE =
  'Your profile is under approval. Please wait up to 24 hours — we will notify you by email.';

const sanitizeMessage = (text) =>
  String(text || '')
    .replace(/\bSMS\b/gi, '')
    .replace(/\s+and\s+email/gi, ' email')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+—\s+/g, ' — ')
    .trim();

const AccountPendingApproval = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useContext(LoginContext);
  const message = sanitizeMessage(location.state?.message || DEFAULT_MESSAGE);

  const goToLogin = (e) => {
    e.preventDefault();
    logout();
    navigate('/login?force=1', {
      state: {
        message: 'If your account was approved, please log in with your mobile and password.',
      },
    });
  };

  return (
    <div className="hs-pending-page">
      <style>{`
.header-container .hom-top {
  top: 0 !important;
  left: 0;
  right: 0;
}

.hs-pending-page {
  width: 100%;
  /* Fill viewport; padding-top clears absolute navbar + gap so card never sticks */
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 7.5rem 1.25rem 4rem;
  margin: 0;
  box-sizing: border-box;
  background: linear-gradient(180deg, #faf7f5 0%, #f5f0ec 100%);
  clear: both;
  position: relative;
  z-index: 1;
  float: none;
}

@media (max-width: 991.98px) {
  /* Mobile: header is in normal flow — smaller top padding */
  .hs-pending-page {
    min-height: calc(100vh - 120px);
    padding: 2.5rem 1rem 3rem;
  }
}

.hs-pending-card {
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid rgba(229, 61, 92, 0.08);
  border-radius: 18px;
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.02),
    0 14px 36px rgba(40, 20, 30, 0.07);
  padding: 1.5rem 1.4rem 1.25rem;
  text-align: center;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
}

.hs-pending-card * {
  box-sizing: border-box;
  text-transform: none !important;
}

.hs-pending-icon-wrap {
  position: relative;
  width: 56px;
  height: 56px;
  margin: 0 auto 0.75rem;
}

.hs-pending-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(145deg, #fff0f3 0%, #ffe4ea 100%);
  color: #E53D5C;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 0 1px rgba(229, 61, 92, 0.1);
}

.hs-pending-icon svg {
  width: 26px;
  height: 26px;
}

.hs-pending-spark {
  position: absolute;
  color: #E53D5C;
  font-size: 0.6rem;
  font-weight: 700;
  line-height: 1;
  opacity: 0.85;
  pointer-events: none;
}

.hs-pending-spark.s1 { top: 0; right: -2px; }
.hs-pending-spark.s2 { bottom: 4px; left: -2px; }
.hs-pending-spark.s3 { top: 14px; left: -8px; font-size: 0.5rem; }

.hs-pending-card h1 {
  margin: 0 0 0.4rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: #1a2340;
  line-height: 1.25;
  letter-spacing: -0.02em;
}

.hs-pending-card .hs-pending-msg {
  margin: 0 0 1rem;
  font-size: 0.88rem;
  color: #5c6478;
  line-height: 1.5;
}

.hs-pending-card .hs-pending-msg em {
  font-style: normal;
  color: #E53D5C;
  font-weight: 600;
}

.hs-pending-steps {
  --step-size: 32px;
  list-style: none;
  margin: 0 0 1.1rem;
  padding: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
}

.hs-pending-steps::before {
  content: '';
  position: absolute;
  left: calc((var(--step-size) / 2) - 1px);
  top: calc(var(--step-size) / 2);
  bottom: calc(var(--step-size) / 2);
  width: 2px;
  background-image: linear-gradient(
    to bottom,
    #d8dce8 0 55%,
    transparent 55% 100%
  );
  background-size: 2px 10px;
  background-repeat: repeat-y;
  z-index: 0;
}

.hs-pending-steps li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.45rem 0;
  position: relative;
  z-index: 1;
}

.hs-pending-step-icon {
  flex-shrink: 0;
  width: var(--step-size);
  height: var(--step-size);
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef2ff;
  color: #4f6bed;
  position: relative;
  z-index: 1;
  line-height: 0;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px rgba(79, 107, 237, 0.12);
}

.hs-pending-steps li.is-done .hs-pending-step-icon {
  background: #e8f8ef;
  color: #1f9d57;
  box-shadow: inset 0 0 0 1px rgba(31, 157, 87, 0.18);
}

.hs-pending-steps li.is-current .hs-pending-step-icon {
  background: #fff6e8;
  color: #d97706;
  box-shadow: inset 0 0 0 1px rgba(217, 119, 6, 0.2);
}

.hs-pending-step-icon svg {
  width: 15px !important;
  height: 15px !important;
  display: block;
  flex-shrink: 0;
  max-width: none;
  overflow: visible;
}

.hs-pending-steps strong {
  display: block;
  font-size: 0.86rem;
  font-weight: 650;
  color: #1a2340;
  line-height: 1.25;
  margin: 0 0 0.1rem;
}

.hs-pending-steps li > div {
  padding-top: 0.2rem;
  min-width: 0;
}

.hs-pending-steps li > div span {
  display: block;
  font-size: 0.76rem;
  color: #7a8296;
  line-height: 1.35;
}

.hs-pending-actions {
  display: flex;
  gap: 0.55rem;
  margin-bottom: 0.85rem;
}

.hs-pending-actions a {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  text-align: center;
  text-decoration: none;
  font-size: 0.86rem;
  font-weight: 600;
  padding: 0.65rem 0.7rem;
  border-radius: 10px;
  line-height: 1.2;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}

.hs-pending-actions a svg {
  width: 15px;
  height: 15px;
  display: block;
  flex-shrink: 0;
}

.hs-pending-home {
  background: #fff;
  color: #E53D5C !important;
  border: 1.5px solid #E53D5C;
}

.hs-pending-home:hover {
  background: #fff5f7;
}

.hs-pending-support {
  background: #E53D5C;
  color: #fff !important;
  border: 1.5px solid #E53D5C;
  box-shadow: 0 4px 12px rgba(229, 61, 92, 0.25);
}

.hs-pending-support:hover {
  background: #d43552;
}

.hs-pending-contact {
  margin: 0;
  font-size: 0.74rem;
  color: #8b93a7;
  line-height: 1.45;
}

.hs-pending-contact a {
  color: #E53D5C;
  text-decoration: none;
  font-weight: 600;
}

.hs-pending-contact a:hover {
  text-decoration: underline;
}

@media (max-width: 480px) {
  .hs-pending-page {
    min-height: calc(100vh - 100px);
    padding: 2rem 1rem 2.5rem;
  }

  .hs-pending-card {
    padding: 1.35rem 1.15rem 1.15rem;
    border-radius: 16px;
  }

  .hs-pending-card h1 {
    font-size: 1.2rem;
  }

  .hs-pending-actions {
    flex-direction: column;
  }
}
`}</style>

      <article className="hs-pending-card" aria-labelledby="pending-title">
        <div className="hs-pending-icon-wrap" aria-hidden="true">
          <div className="hs-pending-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="6" y="3" width="12" height="18" rx="2" />
              <path d="M9 7h6" />
              <circle cx="12" cy="13" r="3.2" />
              <path d="M12 11.5v1.7l1.1.7" />
            </svg>
          </div>
          <span className="hs-pending-spark s1">+</span>
          <span className="hs-pending-spark s2">+</span>
          <span className="hs-pending-spark s3">+</span>
        </div>

        <h1 id="pending-title">Profile under review</h1>
        <p className="hs-pending-msg">
          {message.includes('email') ? (
            <>
              {message.split(/email/i)[0]}
              <em>email</em>
              {message.split(/email/i).slice(1).join('email')}
            </>
          ) : (
            message
          )}
        </p>

        <ul className="hs-pending-steps">
          <li className="is-done">
            <span className="hs-pending-step-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="5.5 12.5 10 17 18.5 7.5" />
              </svg>
            </span>
            <div>
              <strong>Registration received</strong>
              <span>Your details are saved securely.</span>
            </div>
          </li>
          <li className="is-current">
            <span className="hs-pending-step-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="8.25" />
                <path d="M12 8.25v4.1l2.75 1.65" />
              </svg>
            </span>
            <div>
              <strong>Admin review</strong>
              <span>Usually within 24 hours our team is reviewing.</span>
            </div>
          </li>
          <li>
            <span className="hs-pending-step-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
                <path d="M4 7.5l8 6.25L20 7.5" />
              </svg>
            </span>
            <div>
              <strong>We’ll notify you</strong>
              <span><em style={{ color: '#E53D5C', fontStyle: 'normal', fontWeight: 600 }}>Email</em> when your profile is approved.</span>
            </div>
          </li>
        </ul>

        <div className="hs-pending-actions">
          <Link to="/" className="hs-pending-home">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 11.5L12 4l9 7.5" />
              <path d="M6 10.5V20h12v-9.5" />
            </svg>
            Home
          </Link>
          <a href="/login?force=1" className="hs-pending-support" onClick={goToLogin}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
            Login
          </a>
        </div>

        <p className="hs-pending-contact">
          Need help? <a href="/contact-us">Contact Support</a>
          {' - '}
          <a href="mailto:support@hindusanjog.com">support@hindusanjog.com</a>
        </p>
      </article>
    </div>
  );
};

export default AccountPendingApproval;
